/**
 * District palette: floats along the bottom of the map while a plan is open.
 *
 * One chip per district — its color, number, and a fill gauge showing how
 * close it is to the ideal population — so picking the next district and
 * watching balance happen in the same glance, right where the brush is.
 * Keys 1–9 select districts too (handled by the paint tool).
 */
import { useMemo } from 'react';
import { readableTextOn } from '../../lib/districtColors';
import { computeDistrictStats, formatPopulation } from '../../lib/districtStats';
import { selectIdealPopulation, usePaintingStore } from '../../stores/painting';
import { useTenantStore } from '../../stores/tenant';

const DEFAULT_MAX_DEVIATION_PCT = 10;

function gaugeColor(absPct: number, threshold: number): string {
  if (absPct <= threshold / 2) return '#1E9E78';
  if (absPct <= threshold) return '#D9A21B';
  return '#D1543A';
}

export function DistrictPalette(): JSX.Element | null {
  const tenant = useTenantStore((s) => s.tenant);
  const planId = usePaintingStore((s) => s.planId);
  const districtCount = usePaintingStore((s) => s.districtCount);
  const colors = usePaintingStore((s) => s.colors);
  const labels = usePaintingStore((s) => s.labels);
  const activeDistrict = usePaintingStore((s) => s.activeDistrict);
  const paintMode = usePaintingStore((s) => s.paintMode);
  const setActiveDistrict = usePaintingStore((s) => s.setActiveDistrict);
  const setPaintMode = usePaintingStore((s) => s.setPaintMode);
  const assignments = usePaintingStore((s) => s.assignments);
  const populations = usePaintingStore((s) => s.populations);
  const populationsReady = usePaintingStore((s) => s.populationsState === 'ready');
  const totalPopulation = usePaintingStore((s) => s.totalPopulation);
  const ideal = usePaintingStore(selectIdealPopulation);
  const threshold = tenant?.districts.max_population_deviation_pct ?? DEFAULT_MAX_DEVIATION_PCT;

  const stats = useMemo(
    () => computeDistrictStats(assignments, populations, districtCount),
    [assignments, populations, districtCount],
  );
  const unassignedPop = useMemo(() => {
    if (!populationsReady) return 0;
    return Math.max(0, totalPopulation - stats.reduce((sum, s) => sum + s.population, 0));
  }, [stats, totalPopulation, populationsReady]);

  if (!planId || districtCount === 0) return null;

  return (
    <div
      className="floating flex max-w-full items-stretch gap-1 overflow-x-auto p-1.5"
      role="radiogroup"
      aria-label="District to paint"
    >
      {stats.map((stat) => {
        const i = stat.district;
        const color = colors[i] ?? '#78716c';
        const label = labels[i] ?? `District ${i + 1}`;
        const active = i === activeDistrict && paintMode !== 'erase';
        const share = ideal > 0 ? stat.population / ideal : 0;
        const absDev = Math.abs(stat.deviationPct);
        return (
          <button
            key={i}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => {
              setActiveDistrict(i);
              if (paintMode !== 'paint') setPaintMode('paint');
            }}
            title={`${label}${populationsReady ? ` — ${formatPopulation(stat.population)} people (${Math.round(share * 100)}% of ideal)` : ''}${i < 9 ? ` · key ${i + 1}` : ''}`}
            className={`group flex min-w-[64px] shrink-0 flex-col gap-1 rounded-lg px-2 py-1.5 text-left transition ${
              active ? 'bg-white shadow-sm ring-2' : 'hover:bg-stone-100'
            }`}
            style={active ? ({ '--tw-ring-color': color } as React.CSSProperties) : undefined}
          >
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="flex h-6 w-6 items-center justify-center rounded-lg text-[11px] font-bold shadow-sm"
                style={{ backgroundColor: color, color: readableTextOn(color) }}
              >
                {i + 1}
              </span>
              <span className="sr-only">{label}</span>
              {populationsReady && (
                <span className="text-[11px] font-semibold tabular-nums text-stone-600" aria-hidden="true">
                  {Math.round(share * 100)}%
                </span>
              )}
            </span>
            {populationsReady && (
              <span className="relative block h-1.5 w-full overflow-hidden rounded-full bg-stone-200" aria-hidden="true">
                <span
                  className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-300"
                  style={{
                    width: `${Math.min(100, share * 100)}%`,
                    backgroundColor: stat.population > 0 ? gaugeColor(absDev, threshold) : 'transparent',
                  }}
                />
                {/* ideal marker at 100% is the bar's end; overfull shows red */}
              </span>
            )}
          </button>
        );
      })}
      {populationsReady && (
        <div className="ml-1 flex shrink-0 flex-col justify-center border-l border-stone-200 pl-2.5 pr-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Unassigned</span>
          <span className="text-xs font-semibold tabular-nums text-stone-700">
            {formatPopulation(unassignedPop)}
          </span>
        </div>
      )}
    </div>
  );
}
